<template>
  <div>
    <div v-if="loading" class="text-center">
      <v-skeleton-loader type="date-picker"></v-skeleton-loader>
    </div>
    <div>
      <v-tabs v-model="activeTab" show-arrows>
        <v-tab :key="'calendar'" ripple @click="clickTab(0)">{{ $t('message.calendar') }}</v-tab>
        <v-tab :key="'public_events'" ripple @click="clickTab(1)">{{ $t('message.academy_events') }}</v-tab>
        <v-tab :key="'public_schedule'" ripple @click="clickTab(2)">{{ $t('message.schedule') }}</v-tab>
        <v-tab :key="'configuration'" ripple @click="clickTab(3)" v-allowed="'edit_calendar'">{{
          $t("message.configuration") }}</v-tab>

        <v-tabs-items v-model="activeTab">
          <v-tab-item :key="'calendar'" class="pa-3">
            <div v-if="!loading" style="margin-top: 10px">
              <v-card-actions v-if="!isSmallerDisplay()" class="my-6">
                <div>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-badge color="grey" left dot>
                          {{ $t("message.regular_class") }}
                        </v-badge>
                      </span>
                    </template>
                    <span>{{ $t("message.regular_schedule_explained") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-badge left style="margin-left: 20px" :color="getColor('purple')" dot>
                          {{ $t("message.internal_event") }}
                        </v-badge>
                      </span>
                    </template>
                    <span>{{ $t("message.admin_event_explained") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-badge left style="margin-left: 20px" :color="getColor('orange')" dot>
                          {{ $t("message.admin_event_with_visitor") }}
                        </v-badge>
                      </span>
                    </template>
                    <span>{{ $t("message.with_visitor_event_explained") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-badge left style="margin-left: 20px" color="error" dot>
                          {{ $t("message.social_event") }}
                        </v-badge>
                      </span>
                    </template>
                    <span>{{ $t("message.social_event_explained") }}</span>
                  </v-tooltip>
                </div>
                <v-spacer></v-spacer>
              </v-card-actions>
              <div v-if="professors">
                <members-autocomplete :hide-details="true" :label="$t('message.filter_calendar_professor')"
                  v-model="selectedProfessor" :items="professors" />
              </div>
              <v-card-actions>
                <v-switch v-show="false" v-model="multiSelect" :label="$t('message.multi_select_dates')"></v-switch>
                <v-btn elevation="0" small :loading="loadingCalendar" @click="loadCalendar()">
                  <v-icon class="mr-1">mdi-sync</v-icon>
                  {{ $t("message.refresh") }}
                </v-btn>
                <v-spacer></v-spacer>
                {{ calendarTimeZone }}
              </v-card-actions>
              <v-card-text>
                <div v-if="multiSelect" class="supporting-text">
                  <div>{{ $t("message.click_multiple_dates_explained") }}</div>
                </div>
                <div v-else-if="addingVisitor">
                  <v-alert type="info">{{ $t("message.adding_visitor_explained", [addingVisitor.name]) }}</v-alert>
                </div>
                <span v-else class="supporting-text">{{ $t("message.click_date_explained") }}</span>
              </v-card-text>
              <v-card-actions v-if="multiSelect">
                <v-btn outlined @click="showAddMultipleEvents()" :disabled="filteredEvents.length < 2" elevation="0"
                  color="primary" small>
                  <v-icon>mdi-plus-circle-outline</v-icon>
                  {{ $t("message.add_multiple") }}
                </v-btn>
              </v-card-actions>
              <div :class="{ 'dark-calendar': isDarkMode() }">
                <div ref="calendar" class="calendar-div">
                </div>
              </div>
            </div>
          </v-tab-item>
          <v-tab-item :key="'public_events'" class="pa-3">
            <v-card class="pa-0" v-allowed="'edit_schedule'" elevation="0">
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn outlined small elevation="0" v-allowed="'manage_event'" @click="goToNewEvent()" color="primary">
                  {{ $t("message.add_event") }}
                </v-btn>
              </v-card-actions>
              <v-skeleton-loader v-if="loadingPublicEvents"
                type="table-heading, list-item-two-line"></v-skeleton-loader>
              <v-list>
                <v-list-group :value="true">
                  <template v-slot:activator>
                    <v-list-item-title>
                      {{ $t("message.upcoming_events") }} ({{ publicEvents.length }})
                    </v-list-item-title>
                  </template>
                  <div v-if="publicEvents.length > 0">
                    <v-list-item v-for="(i, index) in publicEvents" :key="'public_event_' + index"
                      class="text-left ml-3" @click="goToAcademyEvent(i._id)">
                      <v-list-item-avatar v-if="i.image">
                        <ImageDisplay :src="i.image" />
                      </v-list-item-avatar>
                      <v-list-item-action v-else>
                        <span class="ml-2 mb-1 mr-1">
                          <v-icon>mdi-calendar-month</v-icon>
                        </span>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ i.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $d(new Date(i.date), 'long', $i18n.locale) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </div>
                  <v-card-text v-else class="text-h6 text-center">
                    {{ $t("message.no_data") }}
                  </v-card-text>
                </v-list-group>
                <v-list-group v-if="pastPublicEvents.length > 0">
                  <template v-slot:activator>
                    <v-list-item-title>
                      {{ $t("message.past_events") }} ({{ pastPublicEvents.length }})
                    </v-list-item-title>
                  </template>
                  <v-list-item v-for="(i, index) in pastPublicEvents" :key="'canceled_event_' + index"
                    class="text-left ml-3" @click="goToAcademyEvent(i._id)">
                    <v-list-item-avatar v-if="i.image">
                      <ImageDisplay :src="i.image" />
                    </v-list-item-avatar>
                    <v-list-item-action v-else>
                      <span class="ml-2 mb-1 mr-1">
                        <v-icon>mdi-calendar-month</v-icon>
                      </span>

                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ i.title }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ $d(new Date(i.date), 'long', $i18n.locale) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-group>
                <v-list-group v-if="canceledPublicEvents.length > 0">
                  <template v-slot:activator>
                    <v-list-item-title>{{ $t("message.canceled_events") }}
                      ({{ canceledPublicEvents.length }})</v-list-item-title>
                  </template>
                  <v-list-item v-for="(i, index) in canceledPublicEvents" :key="'canceled_event_' + index"
                    class="text-left ml-3" @click="goToAcademyEvent(i._id)">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ i.title }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ $d(new Date(i.date), 'long', $i18n.locale) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-group>
              </v-list>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'public_schedule'" class="pa-3" v-allowed="'edit_calendar'">
            <v-card class="pa-0" v-allowed="'edit_schedule'" elevation="0" outlined>
              <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn outlined elevation="0" small color="primary" @click="updateScheduleNotes('update')">{{
                  $t('message.update')
                }}</v-btn>
                <v-btn elevation="0" small @click="updateScheduleNotes()">{{ $t('message.clear')
                }}</v-btn>
              </v-card-actions>
              <v-card-text>
                <v-avatar size="20" v-if="scheduleNotes && scheduleNotes.user">
                  <ImageDisplay v-if="scheduleNotes.user.userImageSmall" :src="scheduleNotes.user.userImageSmall" />
                  <img v-else :src="assets('default-profile-avatar.png')" />
                </v-avatar>
                <span class="ml-1" v-if="scheduleNotes && scheduleNotes.user">{{ $t('message.last_update_by') }}
                  {{ scheduleNotes.user.displayName }}</span>
                <span v-if="scheduleNotes && scheduleNotes.user" style="color:gray; margin-left: 10px">{{ $d(new
                  Date(scheduleNotes.date), 'short', $i18n.locale) }}</span>
                <v-textarea style="margin-top:10px" rows="3" :auto-grow="true" outlined v-model="scheduleNotes.notes"
                  :label="$t('message.schedule_notes')"></v-textarea>
                <div class="supportive-text" style="margin-top:-20px">{{ $t('message.schedule_notes_explained') }}</div>
              </v-card-text>
            </v-card>
            <v-card v-if="!loading && academy" class="bjjlink-card" elevation="0" outlined>
              <div xs12 fill-height>
                <schedule-selector></schedule-selector>
              </div>
            </v-card>
            <alert-message :text="updateMessage" />
          </v-tab-item>
          <v-tab-item :key="'configuration'" class="pa-3" v-allowed="'edit_calendar'">
            <v-card elevation="0" outlined>
              <div class="text-subtitle-1 pa-2 pb-0">
                {{ $t("message.registration_based_classes") }}
              </div>
              <div class="pa-2">
                <v-switch @change="updateCalendarSettings" hide-details v-model="showForAll"
                  :label="$t('message.visible_for_all')"></v-switch>
                <div class="supporting-text" style="margin-left:20px">{{ $t('message.public_calendar_explained') }}
                </div>
                <v-switch v-show="false" hide-details v-model="showForStudents"
                  :label="$t('message.visible_for_students')"></v-switch>
                <v-switch @change="updateCalendarSettings" hide-details v-model="skipApproval"
                  :label="$t('message.skip_class_request_approval')"></v-switch>
                <div class="supporting-text" style="margin-left:20px">{{
                  $t('message.skip_class_request_approval_explained') }}
                </div>
                <v-switch @change="updateCalendarSettings" hide-details v-model="allowWaitlist"
                  :label="$t('message.allow_waitlist')"></v-switch>
                <div class="supporting-text" style="margin-left:20px">{{
                  $t('message.allow_waitlist_explained') }}
                </div>
              </div>
            </v-card>
            <v-card elevation="0" outlined class="bjjlink-card">
              <div class="text-subtitle-1 pa-2">
                {{ $t("message.public_schedule_page") }}
                <div class="supporting-text">{{ $t('message.embed_schedule_explained') }}</div>
              </div>
              <v-card-text>
                <v-select hide-details v-model="scheduleMode" :items="scheduleModeItems" item-text="text"
                  item-value="value" :label="$t('message.schedule_display')" outlined dense></v-select>
                <v-switch @change="refreshCalendarIframe" hide-details v-model="embedMode"
                  :label="$t('message.hide_logos')"></v-switch>
                <div class="text-caption">
                  {{ $t('message.hide_logos_explained') }}
                </div>
                <v-switch @change="refreshCalendarIframe" hide-details v-model="transparentBackground"
                  :label="$t('message.transparent_background')"></v-switch>
                <div class="text-caption">
                  {{ $t('message.transparent_background_explained') }}
                </div>
              </v-card-text>
              <v-card-text v-if="displayExternalSchedule" id="academy_public_schedule_embed">
                <iframe v-if="publicScheduleUrl" style="width:100%;height:330px" :src="publicScheduleUrl"></iframe>
              </v-card-text>
              <v-card-text class="text-center" v-else>
                <circles-loader></circles-loader>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn v-if="!isSmallerDisplay()" elevation="0" small @click="openQrCodeNewPage()">
                  {{ $t('message.qr_code') }}
                </v-btn>
                <v-btn elevation="0" small @click="copyPublicUrl()">
                  {{ $t('message.copy_url') }}
                </v-btn>
                <v-btn elevation="0" small @click="copyEmbedCode(false)">
                  {{ $t('message.copy_embed_code') }}
                </v-btn>
              </v-card-actions>
            </v-card>
            <v-card elevation="0" v-show="false" outlined class="bjjlink-card">
              <v-card-title>
                {{ $t("message.public_calendar_page") }}
              </v-card-title>
              <v-card-text>
                <span class="supporting-text">{{ $t('message.embed_calendar_explained') }}</span>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn elevation="0" small @click="copyPublicUrl(true)">
                  {{ $t('message.copy_url') }}
                </v-btn>
                <v-btn elevation="0" small @click="copyEmbedCode(true)">
                  {{ $t('message.copy_embed_code') }}
                </v-btn>
              </v-card-actions>
            </v-card>
            <v-card elevation="0" outlined class="bjjlink-card">
              <div class="text-subtitle-1 pa-2">
                {{ $t("message.book_class_public_page") }}
                <div class="supporting-text">{{ $t('message.embed_schedule_visit_explained') }}</div>
              </div>
              <div class="pa-2">
                <div>
                  <AvailableClasses :single="true" :label="$t('message.single_class_calendar')" :vm="this"
                    v-model="scheduleClassName">
                  </AvailableClasses>
                  <span class="supporting-text">{{ $t('message.embed_schedule_visit_class_explained') }}</span>
                </div>
                <v-switch @change="updateCalendarSettings" hide-details v-model="disablePublicBookClassPage"
                  :label="$t('message.disable_page')"></v-switch>
              </div>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn elevation="0" small @click="copyPublicUrl(true, true)">
                  {{ $t('message.copy_url') }}
                </v-btn>
                <v-btn elevation="0" small @click="copyEmbedCodeScheduleVisit">
                  {{ $t('message.copy_embed_code') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </div>
    <v-dialog v-model="calendarEventDialog" max-width="90%" v-if="selectedEvent || filteredEvents.length > 2">
      <v-card v-if="loadingCalendar" class="pa-2" elevation="0" outlined>
        <v-card-text class="text-center">
          <div>
            {{ $t('message.loading_calendar') }}
          </div>
          <div>
            <v-progress-circular size="30" indeterminate color="secondary"></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-actions v-if="selectedEvent" v-allowed="'edit_calendar'">
          <v-btn small elevation="0" @click.stop="closeCalendarEventDialog()">
            {{ $t("message.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else class="pa-2" elevation="0" outlined>
        <v-card-actions v-if="selectedEvent" class="pb-0 mb-0">
          <v-switch hide-details class="mr-2" v-if="!selectedEvent.vacation" :label="$t('message.disabled')"
            v-model="selectedEvent.disabled"></v-switch>
          <v-switch hide-details v-if="!selectedEvent.disabled" :label="$t('message.vacation')"
            v-model="selectedEvent.vacation"></v-switch>
        </v-card-actions>
        <v-card-title class="text-h6" v-if="selectedEvent && !selectedEvent.addingMultiple">
          {{ $d(selectedEvent.date, "month_day_year", $i18n.locale) }}
        </v-card-title>
        <div v-if="selectedEvent && !selectedEvent.disabled">
          <v-card-text class="pb-0">
            <v-text-field :disabled="selectedEvent.existing ? true : false" width="100%" outlined dense
              v-model="selectedEvent.title" :label="$t('message.title')"></v-text-field>
            <members-autocomplete :label="$t('message.professor')" v-model="selectedEvent.professorUser" />
            <v-text-field v-if="!selectedEvent.vacation" width="20%" v-model="selectedEvent.maxCapacity" type="number"
              outlined dense :label="$t('message.max_capacity')"></v-text-field>
            <v-text-field hide-details v-if="!selectedEvent.vacation" type="number" width="20%" outlined dense
              v-model="selectedEvent.duration"
              :label="$t('message.duration') + ' (' + $t('message.minutes') + ')'"></v-text-field>
            <v-switch hide-details v-if="!selectedEvent.vacation" v-model="selectedEvent.allowNonMembers"
              :label="$t('message.allow_non_members_join')"></v-switch>
            <div class="text-caption">{{ $t('message.allow_non_members_join_explained') }}</div>
            <v-switch hide-details v-if="!selectedEvent.vacation" v-model="selectedEvent.skipApproval"
              :label="$t('message.skip_approval')"></v-switch>
            <div class="text-caption">{{ $t('message.skip_approval_explained') }}</div>
            <v-textarea class="mt-2" rows="2" width="100%" outlined v-model="selectedEvent.notes"
              :label="$t('message.notes')"></v-textarea>
            <VisitorsAutocomplete :visitors="selectedEvent.visitors" :multiple="true" :label="$t('message.add_visitor')"
              v-model="selectedEvent.visitors" />
          </v-card-text>
          <v-card-text class="text-left pt-0 mt-0">
            <div>
              <div class="text-subtitle-2">{{ $t("message.date") }}:</div>
              <DatePicker :disabled="selectedEvent.existing ? true : false" :show-time="true"
                v-model="selectedEvent.date" :past-event="false">
              </DatePicker>
              <span class="text-caption" v-if="selectedEvent.existing">{{ $t("message.cant_edit_date") }}</span>
            </div>
            <div v-if="selectedEvent.vacation">
              <div class="text-subtitle-2">{{ $t("message.end_date") }}:</div>
              <DatePicker :show-time="false" v-model="selectedEvent.endDate" :past-event="false">
              </DatePicker>
            </div>
          </v-card-text>
        </div>
        <v-card-text v-else-if="selectedEvent">
          <div class="text-subtitle-2">{{ $t("message.date") }}:</div>
          <DatePicker :disabled="selectedEvent.existing ? true : false" :show-time="true" v-model="selectedEvent.date"
            :past-event="false">
          </DatePicker>
          <span class="text-caption" v-if="selectedEvent.existing">{{ $t("message.cant_edit_date") }}</span>
        </v-card-text>
        <v-card-actions v-if="selectedEvent" v-allowed="'edit_calendar'">
          <v-btn :loading="addingCalendarEvent" outlined v-if="!selectedEvent.existing" color="primary" elevation="0"
            small @click="addCalendarEvent()">{{
              multiSelect ? $t("message.add_multiple") + ' (' + this.filteredEvents.length + ')' : $t("message.add")
            }}</v-btn>
          <v-btn :loading="addingCalendarEvent" outlined v-else color="primary" elevation="0" small
            @click="addCalendarEvent()">{{ $t("message.update")
            }}</v-btn>
          <v-btn color="error" elevation="0" small v-if="selectedEvent.id != ''" @click.stop="removeEvent()">{{
            $t("message.delete") }}</v-btn>
          <v-btn small elevation="0" @click.stop="closeCalendarEventDialog()">
            {{ $t("message.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AlertMessage from "@/components/displays/AlertMessage.vue";
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import MembersAutocomplete from "@/components/inputs/MembersAutocomplete.vue";
import AvailableClasses from "@/components/inputs/ScheduleClassesCombo.vue";
import ScheduleSelector from "@/components/inputs/ScheduleSelector.vue";
import VisitorsAutocomplete from "@/components/inputs/VisitorsAutocomplete.vue";
import Api from "@/services/Api2";
import { Environment } from "@/services/Environment";

export default {
  data() {
    return {
      urlTab: this.$route.params.tabId,
      calendarReady: false,
      activeTab: 0,
      calendarInstance: null,
      //calendarPlugins: [dayGridPlugin, interactionPlugin],
      loading: true,
      multiSelect: false,
      calendarLocale: null,
      calendarTimeZone: null,
      timezoneOffset: null,
      professors: null,
      events: [],
      embedMode: false,
      transparentBackground: false,
      visitorId: this.$route.query.v,
      addingVisitor: null,
      filteredEvents: [],
      selectedProfessor: null,
      skipApproval: false,
      allowWaitlist: false,
      disablePublicBookClassPage: false,
      showForAll: false,
      showForStudents: true,
      loadingCalendar: false,
      scheduleClassName: null,
      scheduleMode: 'dynamic',
      selectedEvent: null,
      calendarEventDialog: false,
      allowedMinutes: [0, 15, 30, 45],
      academy: null,
      schedule: null,
      scheduleNotes: {},
      updateMessage: null,
      publicScheduleUrl: null,
      publicCalendarUrl: null,
      scheduleVisitUrl: null,
      displayExternalSchedule: true,
      publicEvents: [],
      canceledPublicEvents: [],
      pastPublicEvents: [],
      loadingPublicEvents: false,
      addingCalendarEvent: false,
      scheduleModeItems: [{ value: 'dynamic', text: this.$t("message.dynamic") }, { value: 'table', text: this.$t("message.table") }, { value: 'flat', text: this.$t("message.flat") }, { value: 'calendar', text: this.$t("message.calendar") }]
    };
  },
  components: {
    MembersAutocomplete,
    ScheduleSelector,
    AlertMessage,
    CirclesLoader,
    DatePicker,
    AvailableClasses,
    VisitorsAutocomplete
  },
  created() {
    this.checkAcademyStatus();
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        if (response.data.calendarSettings) {
          this.showForAll = response.data.calendarSettings.showForAll;
          this.skipApproval = response.data.calendarSettings.skipApproval;
          this.allowWaitlist = response.data.calendarSettings.allowWaitlist;
          this.showForStudents = true; //esponse.data.calendarSettings.showForStudents;
          this.disablePublicBookClassPage = response.data.calendarSettings.disablePublicBookClassPage;
          this.loading = false;
        }
      })
      .catch((e) => {
        if (e)
          this.showAlert("error", this.$t("message.error_generic"));
      });
    Api()
      .get(
        "calendar/admin/academy/" +
        this.$store.state.managedAcademy +
        "/professors?get_all=true"
      )
      .then((response) => {
        if (response.data && response.data.professors.length > 0) {
          this.professors = response.data.professors;
        }
      });
    Api()
      .get(
        "admins/academy/" +
        this.$store.state.selectedAcademy +
        "?populate=schedule_notes"
      )
      .then((response) => {
        this.academy = response.data;
        const baseUrl = Environment.get("VUE_APP_API_HOST");
        this.publicScheduleUrl = baseUrl + '/pub/academy/' + this.$store.state.selectedAcademy + '/schedule?';
        this.publicCalendarUrl = baseUrl + '/pub/academy/' + this.$store.state.selectedAcademy + '/calendar?';
        this.scheduleVisitUrl = baseUrl + '/pub/academy/' + this.$store.state.selectedAcademy + '/calendar?schedule_visit=true';
        this.scheduleNotes =
          response.data.extraFields && response.data.extraFields.scheduleNotes
            ? response.data.extraFields.scheduleNotes
            : {};
      });
    if (this.visitorId) {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/visitor/" +
          this.visitorId +
          "/data"
        )
        .then(response => {
          if (response.data) {
            this.addingVisitor = { name: response.data.name, _id: response.data._id }
          }
        });
    }
  },
  mounted() {
    if (this.urlTab) {
      try {
        const n = new Number(this.urlTab);
        this.activeTab = n.valueOf();
        if (this.activeTab == 1)
          this.getPublicEvents();
      } catch (err) {
        console.log('Invalid tab: ', this.urlTab)
      }
    }
    this.loadCalendar(true);
  },
  watch: {
    '$route'(to) {
      this.activeTab = to.params.tabId;
    },
    multiSelect: {
      immediate: false,
      handler: function (value) {
        if (value == true) {
          this.filteredEvents = [];
        } else {
          console.log('We shoudl not be here')
          //this.prepareEvents(this.events);
        }
      },
    },
    scheduleMode: {
      immediate: false,
      handler: function () {
        this.refreshCalendarIframe();
      },
    },
    selectedProfessor: {
      immediate: false,
      handler: function (val) {
        this.filterCalendarByProfessor(val);
      },
    },
    showForStudents: {
      immediate: false,
      handler: function () {
        if (!this.showForAll) this.updateCalendarSettings();
      },
    },
  },
  methods: {
    openQrCodeNewPage() {
      //window.open(process.env.VUE_APP_ASSETS_HOME + 'secure/qr/' + encodedUrl + '?sp=waiver');
      const baseUrl = Environment.get("VUE_APP_API_HOST");
      let urlToCopy = baseUrl + '/pub/academy/' + this.$store.state.selectedAcademy + '/schedule?mode=' + this.scheduleMode + (this.embedMode ? '&embed=true' : '') + (this.transparentBackground ? '&tbg=true' : '');
      let buff = new Buffer(urlToCopy);
      const encodedUrl = buff.toString("base64");
      window.open(Environment.get("VUE_APP_ASSETS_HOME") + 'secure/qr/' + encodedUrl + '?sp=schedule');
    },
    refreshCalendarIframe() {
      const split = this.publicScheduleUrl.split('?');
      let embedParam = "";
      let transparentBackgroundParam = "";
      let modeParam = "mode=dynamic";
      if (this.scheduleMode)
        modeParam = "mode=" + this.scheduleMode;
      if (this.embedMode)
        embedParam = '&embed=true';
      if (this.transparentBackground)
        transparentBackgroundParam = '&tbg=true';
      this.publicScheduleUrl = split[0] + '?' + modeParam + embedParam + transparentBackgroundParam;
    },
    loadCalendar(loadAll) {
      this.loadingCalendar = true;
      if (loadAll) {
        let fullCalendarScript = document.createElement('script')
        fullCalendarScript.setAttribute('src', 'https://cdn.jsdelivr.net/npm/fullcalendar@6.1.4/index.global.min.js')
        document.head.appendChild(fullCalendarScript);
        const vm = this;
        fullCalendarScript.addEventListener('load', () => {
          //Locales
          const localeEs = document.createElement("script");
          localeEs.src = 'https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/locales/es.js';
          localeEs.type = 'text/javascript';
          document.head.appendChild(localeEs);

          const localePt = document.createElement("script");
          localePt.src = 'https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/locales/pt.js';
          localePt.type = 'text/javascript';
          document.head.appendChild(localePt);
          if (vm.activeTab == 0)
            vm.getEvents();
        });
      } else {
        this.getEvents();
      }
    },
    initializeCalendar(events) {
      const preparedEvents = this.prepareEvents(events);
      let calendarOptions = {
        initialView: this.isSmallerDisplay() ? 'listWeek' : 'dayGridMonth',
        events: preparedEvents,
        views: {
          listWeek: { buttonText: 'list' },
          dayGridMonth: { buttonText: 'grid' },
        },
      };
      if (this.isSmallerDisplay()) {
        calendarOptions.headerToolbar = {
          left: 'prev,next',
          center: '',
          right: 'listWeek,dayGridMonth'
        };
      }
      this.filteredEvents = preparedEvents;
      let userLang = this.$i18n.locale;
      if (userLang == 'es')
        calendarOptions.locale = userLang;
      else if (userLang == 'pt')
        calendarOptions.locale = userLang;
      const vm = this;

      let calendarEl = this.$refs.calendar;
      /*eslint-disable no-undef*/
      this.calendarInstance = new FullCalendar.Calendar(calendarEl, calendarOptions);

      this.calendarInstance.on('eventClick', function (info) {
        vm.eventClick(info);
      });
      this.calendarInstance.on('dateClick', function (info) {
        vm.dateClick(info);
      });
      /*
      calendar.on('eventDropped', function (info) {
        vm.eventDropped(info);
      });
      */
      this.calendarInstance.render();
    },
    showAddMultipleEvents() {
      this.calendarEventDialog = true;
      this.selectedEvent = {
        id: '',
        addingMultiple: true,
        date: this.filteredEvents ? this.filteredEvents[0].start : new Date()
      };
    },
    clickTab(tab) {
      if (tab != this.activeTab) {
        if (tab == 0) {
          this.getEvents();
        } else if (tab == 1) {
          this.getPublicEvents();
        }
        this.navigateTo({
          name: "calendartabs",
          params: { tabId: tab },
        });
      }
    },
    copyEmbedCode(calendar) {
      let url = calendar ? this.publicCalendarUrl : this.publicScheduleUrl;
      let textToCopy = '<iframe style="width:100%;height:330px" frameBorder="0" src="' + (url) + '"></iframe>';
      try {
        navigator.clipboard.writeText(textToCopy);
        this.showAlert("success", this.$t("message.copied"));
        /* Alert the copied text */
      } catch (err) {
        this.showAlert("error", this.$t("message.oops_not_working"));
      }
    },
    copyEmbedCodeScheduleVisit() {
      let url = this.scheduleVisitUrl;
      if (this.scheduleClassName) {
        const encoded = this.encodeBase64(this.scheduleClassName);
        url = url + '&class=' + encoded;
      }
      let textToCopy = '<iframe style="width:100%;height:330px" frameBorder="0" src="' + url + '&embed=true"></iframe>';
      try {
        navigator.clipboard.writeText(textToCopy);
        this.showAlert("success", this.$t("message.copied"));
        /* Alert the copied text */
      } catch (err) {
        this.showAlert("error", this.$t("message.oops_not_working"));
      }
    },
    copyPublicUrl(calendar, schedule) {
      let urlToCopy = this.publicScheduleUrl;
      if (calendar)
        urlToCopy = this.publicCalendarUrl;
      if (schedule) {
        urlToCopy = this.scheduleVisitUrl;
        if (this.scheduleClassName) {
          const encoded = this.encodeBase64(this.scheduleClassName);
          urlToCopy = urlToCopy + '&class=' + encoded;
        }
      }
      this.copyToClipboard(urlToCopy);
    },
    updateScheduleNotes(val) {
      let notes = null;
      if (val) notes = this.scheduleNotes.notes;
      else this.scheduleNotes = {};
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/schedule/notes",
          {
            notes: notes,
          }
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.updated"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    filterCalendarByProfessor(val) {
      let newEvents = [];

      // 1. If val is present, filter the full set (this.events)
      if (val) {
        newEvents = this.events.filter(e =>
          e.professor && e.professor._id && e.professor._id.toString() == val
        );
      }
      // 2. If val is null (filter cleared), use the original, full set.
      else {
        // We use the entire original array of events (assuming 'this.events' holds the master list)
        newEvents = this.events;
      }

      // 3. Prepare the filtered/full events and update the calendar
      this.updateCalendarEvents(newEvents);
    },
    eventDropped(e) {
      const data = {
        date: e.event.start,
      };
      Api()
        .patch(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/event/" +
          e.event.id,
          data
        )
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    dateClick(e) {
      //Multi select? Hide events
      if (!this.multiSelect) {
        let d = e.date;
        d.setHours(10);
        d.setMinutes(0);
        this.selectedEvent = {
          id: "",
          date: d,
          endDate: new Date(d),
          skipApproval: this.skipApproval
        };
        this.calendarEventDialog = true;
      } else {
        try {
          const start = e.date;
          const now = new Date();
          now.setDate(now.getDate() - 1);
          now.setHours(23);
          now.setMinutes(59);
          now.setSeconds(59);
          if (now < start) {
            let index = 0;
            for (const d of this.filteredEvents) {
              if (d.start.getTime() == start.getTime()) {
                this.filteredEvents.splice(index, 1);
                return;
              }
              index++;
            }
            this.filteredEvents.push({
              start: start,
              block: true,
              title: this.$t("message.selected"),
              editable: false,
            });
          }
        } catch (err) {
          console.log(err)
        }
      }
    },
    goToNewEvent() {
      this.navigateTo({
        name: "manageacademyevent",
        params: { eventId: 'new' },
      });
    },
    eventClick(e) {
      if (this.multiSelect)
        return;//nothing to do here
      if (e.event.id && e.event.startEditable) {
        this.navigateTo({
          name: "manageacademyclass",
          params: { academyClassId: e.event.id },
        });
      } else {
        if (e.event.startEditable) {
          this.selectedEvent = { existing: true };
          this.selectedEvent.id = e.event.id;
          this.selectedEvent.start = e.event.start;
          this.selectedEvent.skipApproval = this.skipApproval;
          this.selectedEvent.allowWaitlist = this.allowWaitlist;
          this.selectedEvent.date = e.event.start;
          this.selectedEvent.title = e.event.extendedProps.titleOriginal
            ? e.event.extendedProps.titleOriginal
            : e.event.title;
          this.selectedEvent.startNumber = e.event.extendedProps.startNumber;
          this.selectedEvent.professor = e.event.extendedProps.professor;
          this.selectedEvent.professorUser = e.event.extendedProps.professor ? e.event.extendedProps.professor._id : null;
          this.selectedEvent.notes = e.event.extendedProps.notes;
          this.selectedEvent.scheduleClassId = e.event.extendedProps.scheduleClassId;
          if (this.visitorId)
            this.selectedEvent.visitors = [this.visitorId];
          this.calendarEventDialog = true;
        } else {
          this.goToAcademyEvent(e.event.id);
        }
      }
    },
    goToAcademyEvent(id) {
      this.navigateTo({
        name: "manageacademyevent",
        params: { eventId: id },
      });
    },
    updateCalendarEvents(events) {
      if (!this.calendarInstance) {
        console.error("Calendar not initialized!");
        return;
      }
      const preparedEvents = this.prepareEvents(events)
      this.calendarInstance.setOption('events', preparedEvents);
      this.filteredEvents = preparedEvents;
    },

    prepareEvents(eventsToDisplay) {
      if (!eventsToDisplay)
        return;
      return eventsToDisplay.map(e => {
        const newE = { ...e };

        let eventDate = newE.date || newE.start;
        newE.display = 'block';
        let d = new Date(eventDate);
        d.setHours(d.getHours() + this.timezoneOffset);

        if ((newE.vacation || newE.isEvent) && newE.endDate) {
          let eventEndDate = newE.endDate;
          let d2 = new Date(eventEndDate);
          d2.setHours(d2.getHours() + this.timezoneOffset);

          if (newE.vacation) {
            d.setHours(0);
            d.setMinutes(0);
            // 🔑 CRITICAL FIX: Explicitly mark as an all-day event
            newE.allDay = true;//Not sure why this has to be like this but works
          }
          newE.end = d2;
        }

        newE.start = d;
        return newE;
      });
    },
    closeCalendarEventDialog() {
      this.calendarEventDialog = false;
      this.selectedEvent = null;
    },
    removeEvent() {
      Api()
        .delete(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/event/" +
          this.selectedEvent.id
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.success"));
            this.getEvents();
            this.closeCalendarEventDialog();
          }
        });
    },
    getEvents() {
      this.loadingCalendar = true;
      Api()
        .get(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/events"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.calendarTimeZone = data.timeZone;
            this.timezoneOffset = this.getTimezoneOffset(data.timeZone);
            this.events = data.events;
            this.initializeCalendar(data.events);
            this.loadingCalendar = false;
          }
        });
    },

    getPublicEvents() {
      this.loadingPublicEvents = true;
      Api()
        .get(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/events/public"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            if (data.events && data.events.length > 0)
              this.publicEvents = data.events;
            if (data.canceledEvents && data.canceledEvents.length > 0)
              this.canceledPublicEvents = data.canceledEvents;
            if (data.pastEvents && data.pastEvents.length > 0)
              this.pastPublicEvents = data.pastEvents;
          }
          this.loadingPublicEvents = false;
        });
    },
    getStartNumber() {
      if (this.selectedEvent.date) {
        const d = new Date(this.selectedEvent.date);
        const hours = d.getHours();
        const minutes = d.getMinutes();
        return parseFloat(hours + "." + String(minutes).padStart(2, "0"));
      }
    },
    addCalendarEvent() {
      if (!this.selectedEvent.date) {
        this.showAlert("error", this.$t("message.add_time"));
        return;
      }
      if (
        this.selectedEvent.maxCapacity &&
        this.selectedEvent.maxCapacity != "" &&
        isNaN(this.selectedEvent.maxCapacity)
      ) {
        this.showAlert("error", this.$t("message.max_capacity_nan"));
        return;
      }
      this.addingCalendarEvent = true;
      if (!this.selectedEvent.startNumber) {
        this.selectedEvent.startNumber = this.getStartNumber();
      }
      if (
        this.selectedEvent.duration &&
        this.selectedEvent.duration != "" &&
        isNaN(this.selectedEvent.duration)
      ) {
        this.selectedEvent.duration = null;
      }
      let multipleDays = null;
      const date = this.selectedEvent.start
        ? this.selectedEvent.start
        : this.selectedEvent.date;
      let adjustedDate = this.adjustTimeZone(new Date(date), this.timezoneOffset, true);
      let adjustedEndDate = null;
      if (this.multiSelect && this.filteredEvents.length > 1) {
        multipleDays = [];
        for (const d of this.filteredEvents) {
          let eventDate = new Date(d.start);
          eventDate.setMinutes(date.getMinutes());
          eventDate.setHours(date.getHours());
          eventDate.setSeconds(date.getSeconds());
          multipleDays.push(eventDate);
        }
      }
      if (this.selectedEvent.vacation) {
        adjustedEndDate = this.adjustTimeZone(new Date(this.selectedEvent.endDate), this.timezoneOffset, true);
        adjustedEndDate.setHours(23);
        adjustedEndDate.setMinutes(50);
      }
      const data = {
        title: this.selectedEvent.title,
        disabled: this.selectedEvent.disabled,
        vacation: this.selectedEvent.vacation,
        professorUser: this.selectedEvent.professorUser,
        duration: this.selectedEvent.duration,
        scheduleClassId: this.selectedEvent.scheduleClassId,
        allowNonMembers: this.selectedEvent.allowNonMembers,
        date: adjustedDate,
        endDate: adjustedEndDate,
        startNumber: this.getStartNumber(),
        bulkAdded: this.multiSelect ? true : false,
        notes: this.selectedEvent.notes,
        days: multipleDays,
        skipApproval: this.selectedEvent.skipApproval,
        allowWaitlist: this.selectedEvent.allowWaitlist,
        maxCapacity: this.selectedEvent.maxCapacity,
        visitors: this.selectedEvent.visitors && this.selectedEvent.visitors.length ? this.selectedEvent.visitors : null
      };
      Api()
        .post(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/event",
          data
        )
        .then((response) => {
          if (response) {
            if (response.data.total > 0)
              this.showAlert("success", this.$t("message.events_created", [response.data.total]));
            else
              this.showAlert("success", this.$t("message.success"));
            this.multiSelect = false;
            this.getEvents();
            this.closeCalendarEventDialog();
            this.addingCalendarEvent = false;
          }
        })
        .catch(() => {
          this.addingCalendarEvent = false;
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateCalendarSettings() {
      const data = {
        settings: {
          skipApproval: !this.skipApproval ? false : this.skipApproval,
          allowWaitlist: !this.allowWaitlist ? false : this.allowWaitlist,
          showForAll: !this.showForAll ? false : this.showForAll,
          showForStudents: !this.showForStudents ? false : this.showForStudents,
          disablePublicBookClassPage: !this.disablePublicBookClassPage ? false : this.disablePublicBookClassPage
        },
      };
      Api().patch(
        "calendar/admin/academy/" +
        this.$store.state.managedAcademy +
        "/settings",
        data
      );
    },
  },
};
</script>

<style lang="scss">
@import "https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/main.min.css";

.calendar-div {
  min-height: 480px;
}

.dark-calendar .fc .fc-list-day-cushion {
  background-color: #4444FF !important;
  /* dark background for dark mode */
  /* white text */
}
</style>
